import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import {
  validatePersonalInfo,
  validateImage,
  validateContactEmail,
} from '@/lib/auth-validation';
import { Button } from '@/components/ui/Button';
import avatarIcon from '../assets/avatar.svg';
import uploadIcon from '../assets/upload.svg';
import githubIcon from '../assets/github.svg';
import globeIcon from '../assets/globe.svg';
import backIcon from '../assets/back.svg';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export interface PersonalValues {
  name: string;
  contactEmail: string;
  githubUsername: string;
  portfolioUrl: string;
  image: File | null;
}
interface Props {
  initialName: string;
  initialEmail?: string;
  initialGithubUsername?: string;
  initialPortfolioUrl?: string;
  initialImage?: File | null;
  onBack: (values: PersonalValues) => void;
  onSubmit: (values: PersonalValues) => void;
  isSubmitting: boolean;
  errorMessage?: string;
}
const fieldClass =
  'w-full h-[44px] px-4 text-sm bg-[--base-surface-1] border border-[--base-border] rounded-[--radi-6] text-[--text-dark-primary] outline-none focus:border-[--state-brand-active]';
export default function PersonalInformationForm(props: Props) {
  const { externalProfile } = useDaoConfig();
  const [name, setName] = useState(props.initialName);
  const [contactEmail, setEmail] = useState(props.initialEmail ?? '');
  const [githubUsername, setGithub] = useState(
    props.initialGithubUsername ?? ''
  );
  const [portfolioUrl, setPortfolio] = useState(
    props.initialPortfolioUrl ?? ''
  );
  const [image, setImage] = useState<File | null>(props.initialImage ?? null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!image) return;
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  const values = () => ({
    name: name.trim(),
    contactEmail: contactEmail.trim(),
    githubUsername: githubUsername.trim(),
    portfolioUrl: portfolioUrl.trim(),
    image,
  });
  const changeImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      validateImage(file);
      setImage(file);
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Invalid image.');
      event.target.value = '';
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const current = values();
    try {
      validatePersonalInfo(
        current.name,
        current.githubUsername,
        current.portfolioUrl
      );
      validateContactEmail(current.contactEmail);
      props.onSubmit(current);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Check your profile.'
      );
    }
  };
  return (
    <form onSubmit={submit} aria-label="Personal information">
      <h1 className="text-[30px] font-bold leading-[42px]">
        Personal Information
      </h1>
      <input
        aria-label="Profile picture file"
        type="file"
        ref={fileInput}
        onChange={changeImage}
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        disabled={props.isSubmitting}
      />
      <div>
        <p className="mb-2 text-base">Profile Picture</p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={props.isSubmitting}
            aria-label="Upload profile picture"
            className="flex h-[126px] !w-[126px] min-w-[126px] max-w-[126px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-[--base-border] bg-[--base-surface-1] focus-visible:outline focus-visible:outline-[--state-brand-active]"
          >
            {preview ? (
              <img
                src={preview}
                alt="Profile preview"
                className="h-full w-full object-cover"
              />
            ) : (
              <img src={avatarIcon} alt="" width={24} height={24} />
            )}
          </button>
          <div className="flex !w-auto min-w-0 flex-1 flex-col items-start gap-4">
            <p className="text-sm leading-5 text-[--text-dark-secondary]">
              PNG, JPEG or WebP.
              <br />
              Maximum 1 MiB.
            </p>
            <Button
              size="sm"
              variant="secondary"
              className="!px-3"
              onClick={() => fileInput.current?.click()}
              disabled={props.isSubmitting}
            >
              <img src={uploadIcon} width={20} height={20} alt="" />
              Upload avatar
            </Button>
          </div>
        </div>
      </div>
      <div>
        <label htmlFor="fullName" className="mb-2 block">
          Name
        </label>
        <input
          id="fullName"
          className={fieldClass}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          disabled={props.isSubmitting}
          autoComplete="name"
          placeholder="Your name"
        />
      </div>
      <div>
        <label htmlFor="contactEmail" className="mb-2 block">
          Email
        </label>
        <input
          id="contactEmail"
          type="email"
          className={fieldClass}
          value={contactEmail}
          onChange={(event) => setEmail(event.target.value)}
          required
          disabled={props.isSubmitting}
          autoComplete="email"
          placeholder="you@example.com"
        />
        <p className="mt-2 text-xs text-[--text-dark-tertiary]">
          Private contact email. Use your username to log in.
        </p>
      </div>
      <div>
        <label htmlFor="githubUser" className="mb-2 block">
          {externalProfile.label}
        </label>
        <div className="relative">
          {externalProfile.icon && externalProfile.icon !== 'ri-github-line' ? (
            <i
              className={`${externalProfile.icon} absolute left-4 top-3 text-lg text-[--text-dark-secondary]`}
              aria-hidden="true"
            />
          ) : (
            <img
              src={githubIcon}
              width={20}
              height={20}
              alt=""
              className="absolute left-4 top-3"
            />
          )}
          <input
            id="githubUser"
            className={`${fieldClass} !pl-11`}
            value={githubUsername}
            onChange={(event) => setGithub(event.target.value)}
            disabled={props.isSubmitting}
            placeholder={externalProfile.placeholder}
          />
        </div>
      </div>
      <div>
        <label htmlFor="portfolio" className="mb-2 block">
          Portfolio link (optional)
        </label>
        <div className="relative">
          <img
            src={globeIcon}
            width={20}
            height={20}
            alt=""
            className="absolute left-4 top-3"
          />
          <input
            id="portfolio"
            type="url"
            className={`${fieldClass} !pl-11`}
            value={portfolioUrl}
            onChange={(event) => setPortfolio(event.target.value)}
            disabled={props.isSubmitting}
            placeholder="https://yourportfolio.com"
          />
        </div>
      </div>
      {(error || props.errorMessage) && (
        <p role="alert" className="text-sm text-red-400">
          {error || props.errorMessage}
        </p>
      )}
      <div className="flex justify-between gap-4">
        <Button
          variant="secondary"
          onClick={() => props.onBack(values())}
          disabled={props.isSubmitting}
        >
          <img src={backIcon} width={24} height={24} alt="" />
          Back
        </Button>
        <Button type="submit" disabled={props.isSubmitting}>
          Continue
        </Button>
      </div>
    </form>
  );
}
